/**
 * /photo-upload?token=...
 *
 * Token-protected page where someone (without login) can drop a new profile
 * photo. The token comes via email magic link triggered from the directory embed.
 */
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { directoryApi } from '../utils/api';
import { Camera, Loader, CheckCircle2, AlertCircle, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ValorizaLogo } from '../components/ValorizaLogo';

export default function PhotoUploadPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [verifying, setVerifying] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  const [preview, setPreview] = useState(null); // dataURL for preview
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setError('Falta el token en la URL');
      setVerifying(false);
      return;
    }
    directoryApi.verifyPhotoToken(token)
      .then(r => setUser(r.data.user))
      .catch(err => setError(err?.response?.data?.detail || 'El enlace no es válido o ha caducado'))
      .finally(() => setVerifying(false));
  }, [token]);

  const onFile = (f) => {
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('Solo JPG, PNG o WebP');
      return;
    }
    if (f.size > 2_000_000) {
      setError('Máximo 2 MB');
      return;
    }
    setError('');
    setFile(f);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setError('');
    try {
      await directoryApi.uploadPhotoWithToken(token, file);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center rounded-2xl border border-destructive/30 bg-destructive/5 p-8" data-testid="photo-upload-error">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-3" />
          <h1 className="text-lg font-semibold">No se puede acceder</h1>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">Solicita un nuevo enlace desde el directorio corporativo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <ValorizaLogo height="h-7" />
          <span className="text-xs text-muted-foreground">Subir foto · Directorio</span>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto w-full px-4 py-8" data-testid="photo-upload-page">
        {done ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/15 dark:border-emerald-900/30 p-6 text-center" data-testid="photo-upload-done">
            <div className="h-16 w-16 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-3 shadow-lg">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-lg font-bold font-heading">¡Foto actualizada!</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Tu foto aparecerá en el Directorio Valoriza en unos segundos.
            </p>
            <p className="text-xs text-muted-foreground mt-3">Ya puedes cerrar esta ventana.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold font-heading">Sube tu foto</h1>
              {user && (
                <p className="text-sm text-muted-foreground mt-1">
                  Hola <strong>{user.full_name}</strong>, sube una imagen para tu ficha del directorio.
                </p>
              )}
            </div>

            {/* Preview */}
            <div className="rounded-2xl border border-dashed border-border bg-card p-6">
              <div className="flex flex-col items-center gap-4">
                {preview ? (
                  <img src={preview} alt="Vista previa" className="h-32 w-32 rounded-2xl object-cover shadow-lg ring-2 ring-white" />
                ) : (
                  user && (
                    <div
                      className="h-32 w-32 rounded-2xl flex items-center justify-center font-bold text-white text-3xl shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${user.avatar_colour}, ${user.avatar_colour}aa)` }}
                    >
                      {user.initials}
                    </div>
                  )
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="photo-upload-input"
                />

                <Button
                  variant="outline"
                  onClick={() => inputRef.current?.click()}
                  className="gap-2"
                  data-testid="photo-upload-pick"
                >
                  <Camera className="h-4 w-4" />
                  {file ? 'Cambiar foto' : 'Elegir foto'}
                </Button>

                {file && (
                  <p className="text-xs text-muted-foreground">
                    {file.name} · {(file.size / 1024).toFixed(0)} KB
                  </p>
                )}

                {error && (
                  <p className="text-xs text-destructive flex items-center gap-1" data-testid="photo-upload-err">
                    <AlertCircle className="h-3.5 w-3.5" /> {error}
                  </p>
                )}
              </div>
            </div>

            <Button
              onClick={upload}
              disabled={!file || uploading}
              className="w-full mt-4"
              data-testid="photo-upload-submit"
            >
              {uploading ? <><Loader className="h-4 w-4 animate-spin mr-1.5" /> Subiendo…</> : <><Upload className="h-4 w-4 mr-1.5" /> Subir foto</>}
            </Button>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              JPG, PNG o WebP · Máx 2 MB · El enlace caduca 60 min después de generado.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
